import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import CustomButton from '../../../components/CustomButton';
import blogimg from '../../../assets/images/blogimg01.png';

const blogs = [
  {
    title: 'No Esperes el Problema: La Importancia de la Asesoría Legal Preventiva',
    author: 'Emilia Montes',
    date: '2024-06-01',
    readTime: '3 min',
    description:
      '¿Sabías que muchos conflictos se pueden evitar con el consejo legal adecuado a tiempo? Descubre cómo la asesoría legal general te brinda la tranquilidad de tomar decisiones informadas en tu vida personal y profesional, protegiéndote antes de que surjan los problemas.',
    image: blogimg,
  },
  {
    title: 'Más Allá del Juicio: Estrategias para la Mediación Obligatoria',
    author: 'Emi Montes',
    date: '2024-05-20',
    readTime: '2 min',
    description:
      'Una buena mediación evita problemas judiciales innecesarios. Aprende cómo entender y aplicar esta estrategia puede cambiar tu situación legal.',
    image: blogimg,
  },
  {
    title: 'Derechos Laborales: Todo lo que Necesitas Saber',
    author: 'Carlos López',
    date: '2024-05-15',
    readTime: '4 min',
    description:
      'Conoce tus derechos laborales fundamentales y cómo protegerte en el ámbito profesional. Una guía completa para empleados y empleadores.',
    image: blogimg,
  },
  {
    title: 'Contratos Digitales: El Futuro Legal',
    author: 'Ana García',
    date: '2024-05-10',
    readTime: '5 min',
    description:
      'La era digital ha transformado la forma en que hacemos contratos. Descubre las nuevas tendencias y regulaciones en el mundo digital.',
    image: blogimg,
  },
];

const BlogCard = ({ blog, index }) => (
  <motion.div
    className="relative w-56 sm:w-64 max-w-sm rounded-xl overflow-hidden group cursor-pointer flex-shrink-0 shadow-md hover:shadow-lg transition-shadow duration-300"
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.4, delay: index * 0.1 }}
    whileHover={{ y: -2, transition: { duration: 0.2 } }}
  >
    <img src={blog.image} alt={blog.title} className="w-full h-96 sm:h-64 md:h-72 object-cover" />
    <div className="absolute inset-0 bg-black bg-opacity-30" />
    <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/40 to-transparent p-3 sm:p-4 flex flex-col justify-start">
      {/* Todo el contenido en la parte superior */}
      <div className="pt-1">
        <h3 className="text-base sm:text-lg font-bold text-white leading-tight drop-shadow-xl line-clamp-3 mb-2">
          {blog.title}
        </h3>
        <span className="text-xs sm:text-sm text-white font-medium drop-shadow-lg">
          {blog.author} •{' '}
          {new Date(blog.date).toLocaleDateString('es-MX', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
          })}{' '}
          • {blog.readTime}
        </span>
      </div>
    </div>
  </motion.div>
);

const BlogCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1200
  );
  const containerRef = useRef(null);

  // Escuchar cambios en el tamaño de ventana
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      setCurrentIndex(0); // Reset al redimensionar
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, []);

  // Calcular el ancho de cada item basado en el viewport actual
  const getItemWidth = () => {
    // w-56 = 224px (mobile), w-64 = 256px (sm+), gap-6 = 24px
    const cardWidth = windowWidth >= 640 ? 256 : 224;
    const gap = 24;
    return cardWidth + gap;
  };

  const currentItemWidth = getItemWidth();

  // Calcular el ancho visible real del contenedor (considerando padding)
  const containerPadding = windowWidth >= 1024 ? 64 : windowWidth >= 640 ? 48 : 32; // lg:px-8, sm:px-6, px-4 * 2
  const maxContainerWidth = Math.min(windowWidth - containerPadding, 1280); // max-w-7xl = 1280px
  const visibleWidth = maxContainerWidth;

  // Calcular el ancho total del contenido (sin gap al final)
  const totalContentWidth = blogs.length * currentItemWidth - 24;

  // Calcular maxIndex y getTranslateX
  let maxIndex, getTranslateX;

  if (totalContentWidth <= visibleWidth) {
    // Si todo el contenido cabe en el viewport, no necesitamos scroll
    maxIndex = 0;
    getTranslateX = () => 0;
  } else {
    // Calcular el desplazamiento máximo real
    const maxTranslate = totalContentWidth - visibleWidth;
    maxIndex = Math.ceil(maxTranslate / currentItemWidth);

    // Función para calcular el desplazamiento preciso
    getTranslateX = index => {
      const standardTranslate = index * currentItemWidth;
      return Math.min(standardTranslate, maxTranslate);
    };
  }

  const scrollToNext = () => {
    if (currentIndex < maxIndex) {
      setCurrentIndex(prev => prev + 1);
    }
  };

  const scrollToPrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    }
  };

  const scrollToIndex = index => {
    setCurrentIndex(Math.min(Math.max(0, index), maxIndex));
  };

  return (
    <div className="relative py-16 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header - Apple Style */}
        <div className="flex items-center justify-between mb-12">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 tracking-tight">
              Publicaciones
            </h2>
          </motion.div>

          <motion.div
            className="flex items-center space-x-3"
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <button
              onClick={scrollToPrev}
              disabled={currentIndex === 0}
              className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200 ${
                currentIndex === 0
                  ? 'bg-gray-100 text-gray-300 cursor-not-allowed'
                  : 'bg-gray-200 text-gray-600 hover:bg-gray-300 hover:text-gray-800'
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>

            <button
              onClick={scrollToNext}
              disabled={currentIndex >= maxIndex}
              className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200 ${
                currentIndex >= maxIndex
                  ? 'bg-gray-100 text-gray-300 cursor-not-allowed'
                  : 'bg-gray-200 text-gray-600 hover:bg-gray-300 hover:text-gray-800'
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </motion.div>
        </div>

        {/* Cards Container */}
        <div className="relative overflow-hidden">
          <motion.div
            ref={containerRef}
            className="flex gap-6 transition-transform duration-500 ease-out"
            style={{
              transform: `translateX(-${getTranslateX(currentIndex)}px)`,
            }}
          >
            {blogs.map((blog, index) => (
              <BlogCard key={index} blog={blog} index={index} />
            ))}
          </motion.div>
        </div>

        {/* Dots Indicator - Minimal Style */}
        <div className="flex justify-center mt-6 space-x-1.5">
          {Array.from({ length: maxIndex + 1 }, (_, index) => (
            <button
              key={index}
              onClick={() => scrollToIndex(index)}
              className={`w-1.5 h-1.5 rounded-full transition-all duration-200 ${
                index === currentIndex ? 'bg-gray-600 scale-125' : 'bg-gray-300 hover:bg-gray-400'
              }`}
            />
          ))}
        </div>

        <motion.div
          className="flex items-center justify-center mt-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <CustomButton text="Ver más" className="px-4" />
        </motion.div>
      </div>
    </div>
  );
};

export default BlogCarousel;
